import { calculatePercentagePositionInARow } from '../helpers'
import { IHealthProceduresOmics, IOmicsPositioned, TimelinePeriodTypes } from '~/interfaces'

interface IGetTimelineRowPosition {
  healthProcedures: IHealthProceduresOmics[]
  mode: TimelinePeriodTypes
}

export interface IOmicsGrouped {
  percentagePosition: number
  startDate: string
  endDate: string
  omics: IOmicsPositioned[]
  differentTypes: boolean
}

type IGroupOmicsStructure = IOmicsGrouped | IOmicsPositioned

export const getTimelineRowPosition = ({ healthProcedures, mode }: IGetTimelineRowPosition): IOmicsPositioned[] => {
  const structuredEllipses = healthProcedures.map(healthProcedure => {
    const percentagePosition = calculatePercentagePositionInARow(healthProcedure.startDate, mode)
    return { ...healthProcedure, percentagePosition: percentagePosition }
  })

  return structuredEllipses
}

export const isEllipseStructured = (ellipse: IGroupOmicsStructure): ellipse is IOmicsPositioned => {
  return 'type' in ellipse
}

export const isEllipseGrouped = (ellipse: IGroupOmicsStructure): ellipse is IOmicsGrouped => {
  return 'omics' in ellipse
}

export const findNextIndexToMerge = (groupedOmics: IGroupOmicsStructure[]): number => {
  const ellipseSize = 10
  const subEllipseSize = 3

  return groupedOmics.findIndex((omicsStructured, index) => {
    const nextOmics = groupedOmics[index + 1]
    const storageOmicsQuantity = isEllipseGrouped(omicsStructured) ? omicsStructured.omics.length : 0
    const totalActualPosition = omicsStructured.percentagePosition + ellipseSize + storageOmicsQuantity * subEllipseSize

    if (nextOmics) {
      const nextOmicsPercentagePosition = nextOmics.percentagePosition
      return totalActualPosition >= nextOmicsPercentagePosition
    }
    return false
  })
}

export function groupEllipsesInARow(structuredOmics: IGroupOmicsStructure[]): IOmicsGrouped[] {
  const groupedEllipses = [...structuredOmics]

  const firstIndexNeedsMerge = findNextIndexToMerge(groupedEllipses)
  const hasOmicsToGroup = firstIndexNeedsMerge > -1

  if (hasOmicsToGroup) {
    const ellipse = groupedEllipses[firstIndexNeedsMerge]
    const nextEllipse = groupedEllipses[firstIndexNeedsMerge + 1] as IOmicsPositioned

    const storageEllipseData = isEllipseGrouped(ellipse) ? ellipse.omics : [ellipse]
    const isOmicsDifferentTypes = storageEllipseData.some(omicsData => nextEllipse.type !== omicsData.type)

    groupedEllipses[firstIndexNeedsMerge] = {
      percentagePosition: ellipse.percentagePosition,
      startDate: storageEllipseData[0].startDate,
      endDate: nextEllipse.endDate,
      omics: [...storageEllipseData, nextEllipse],
      differentTypes: isOmicsDifferentTypes
    }
    groupedEllipses.splice(firstIndexNeedsMerge + 1, 1)
    return groupEllipsesInARow([...groupedEllipses])
  }

  const standardGrouped = groupedEllipses.map(omics => {
    if (isEllipseStructured(omics)) {
      return {
        percentagePosition: omics.percentagePosition,
        startDate: omics.startDate,
        endDate: omics.endDate,
        omics: [omics],
        differentTypes: false
      }
    }
    return omics
  })

  return standardGrouped
}
